/**
 * Audit log vocabulary shared by the API (dashboard aggregation, filters) and the web app (labels).
 * An action is "<category>.<what>", e.g. "org.member.role_changed".
 */
export const AUDIT_CATEGORIES = [
  'auth',
  'user',
  'invitation',
  'org',
  'project',
  'test',
  'run',
  'report',
  'comment',
  'variable',
  'llm',
  'plugin',
  'system',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export const AUDIT_CATEGORY_LABELS: Record<AuditCategory, string> = {
  auth: 'Sign-ins',
  user: 'Users & roles',
  invitation: 'Invitations',
  org: 'Organizations & members',
  project: 'Projects',
  test: 'Tests',
  run: 'Runs',
  report: 'Reports & sharing',
  comment: 'Report comments',
  variable: 'Test data',
  llm: 'AI models',
  plugin: 'Plugins',
  system: 'System',
};

export function auditCategory(action: string): AuditCategory {
  const prefix = action.split('.')[0] ?? '';
  return (AUDIT_CATEGORIES as readonly string[]).includes(prefix) ? (prefix as AuditCategory) : 'system';
}

/**
 * Actions an Admin should notice: access changes, failed sign-ins, public links, credentials and
 * destructive deletes. Shown in the dashboard's "Needs attention" list.
 */
const SENSITIVE_ACTIONS = new Set([
  'auth.login_failed',
  'user.updated',
  'invitation.platform.created',
  'org.member.role_changed',
  'org.member.removed',
  'project.deleted',
  'project.member.removed',
  'report.shared.public',
  'report.deleted',
  'llm.created',
  'llm.updated',
  'llm.deleted',
  'plugin.upserted',
  'plugin.deleted',
  // A schedule or webhook runs tests as its creator; a webhook token is a credential.
  'test.trigger.created',
  'test.trigger.token_rotated',
  // A device agent token lets a computer run the organization's tests on its phones.
  'org.device_agent.created',
  'org.device_agent.removed',
]);

export function isSensitiveAction(action: string): boolean {
  return SENSITIVE_ACTIONS.has(action);
}

export const AUDIT_SENSITIVE_ACTIONS: readonly string[] = [...SENSITIVE_ACTIONS];

/** GET /admin/audit/summary response. */
export interface AuditSummary {
  from: string;
  to: string;
  totals: { events: number; actors: number; orgs: number; sensitive: number; failedLogins: number; logins: number };
  /** One entry per UTC day in the range, oldest first. */
  daily: { date: string; total: number; byCategory: Partial<Record<AuditCategory, number>> }[];
  categories: { category: AuditCategory; count: number }[];
  topActions: { action: string; count: number }[];
  topActors: { actorId: string; name: string; email: string; count: number }[];
  orgs: { orgId: string; name: string; count: number }[];
  /** Busiest hours of the week: [weekday 0=Sun..6][hour 0..23] (UTC). */
  heatmap: number[][];
}
